import { useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import {
  fetchClients,
  fetchEngagements,
  fetchEngagementCompliance,
  fetchEngagementComplianceAttributed,
  fetchAttributedMms,
  type ComplianceCoverage,
  type SeriesPoint,
  type VenueCompliance,
} from "../api/lge.ts";
import { ApiError } from "../api/client.ts";
import type { TabIdentity } from "./GovernanceTab.tsx";

// Monitor tab — LGE increment 4c (LIQ-32, increment 2: KPI overlays).
// Renders measured performance overlaid on the frozen contract's KPI lines
// per the 2026-07-20 scope clarification: spread series vs raw + tick-
// quantized caps with breach shading, depth series vs committed floors
// (anchoring labeled), rolling uptime vs the committed threshold. Score
// cards from increment 1 remain as the summary layer.
//
// LIQ-80 inc 3: MM-attributed overlay. When ROATA read-only-key data
// exists for the client, an MM selector appears; picking an MM overlays
// that MM's OWN attributed series (resting-order spread, per-band
// min-side depth, two-sided quoting presence) on the same charts, scored
// against the selected engagement's frozen contract by the identical
// evaluator. Market-wide view remains the default and is unchanged.
//
// Framing rule (LIQ-31 correction): observed market = incumbent MM +
// organic flow; scores/series attribute to the awarded MM only at go-live.
// The attributed overlay is the exception by construction: it IS the
// named MM's activity, evidenced by their read-only keys.
//
// Palette matches GovernanceTab / analytics cards (hardcoded, house pattern).
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const RED = "#e07070";
const ACCENT = "#6699ff";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

const WINDOW_CHOICES = [7, 14, 30];

const card: CSSProperties = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "14px 16px",
};
const chip = (color: string): CSSProperties => ({
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 5,
  color,
  border: `1px solid ${color}55`,
  whiteSpace: "nowrap",
});
const selectStyle: CSSProperties = {
  background: "rgba(255,255,255,0.05)",
  color: TEXT,
  border: `1px solid ${BORDER}`,
  borderRadius: 5,
  padding: "4px 8px",
  fontSize: 13,
};
const th: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  color: MUTED,
  fontWeight: 500,
  padding: "4px 10px 4px 0",
  borderBottom: `1px solid ${BORDER}`,
};
const td: CSSProperties = {
  fontSize: 13,
  padding: "5px 10px 5px 0",
  borderBottom: `1px solid ${BORDER}`,
};

function venueLabel(v: string): string {
  return v.includes("|") ? v.split("|").join(" · ") : v;
}
function fmtTs(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
}
function fmtHour(iso: string): string {
  return iso.slice(5, 13).replace("T", " ");
}
function scoreColor(s: number | null | undefined): string {
  if (s == null) return MUTED;
  if (s >= 0.9) return GREEN;
  if (s >= 0.6) return AMBER;
  return RED;
}
function fmtScore(s: number | null | undefined): string {
  return s == null ? "—" : `${(s * 100).toFixed(1)}%`;
}
function fmtUsd(v: number | null | undefined): string {
  if (v == null) return "—";
  return v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`;
}
function lastNonNull(
  pts: SeriesPoint[] | undefined,
  get: (p: SeriesPoint) => number | null | undefined,
): number | null {
  if (!pts) return null;
  for (let i = pts.length - 1; i >= 0; i--) {
    const v = get(pts[i]);
    if (v != null) return v;
  }
  return null;
}
function avgNonNull(
  pts: SeriesPoint[] | undefined,
  get: (p: SeriesPoint) => number | null | undefined,
): number | null {
  if (!pts) return null;
  let sum = 0;
  let n = 0;
  for (const p of pts) {
    const v = get(p);
    if (v != null) {
      sum += v;
      n++;
    }
  }
  return n > 0 ? sum / n : null;
}
function uptimeColor(actual: number | null, committed: number | null): string {
  if (actual == null) return MUTED;
  if (committed == null) return TEXT;
  if (actual >= committed) return GREEN;
  if (actual >= committed * 0.95) return AMBER;
  return RED;
}

// Shared echarts axis/grid styling for the dark theme.
const AXIS = {
  axisLine: { lineStyle: { color: BORDER } },
  axisLabel: { color: MUTED, fontSize: 10 },
  splitLine: { lineStyle: { color: BORDER } },
};

function breachAreas(pts: SeriesPoint[]): Array<[{ xAxis: string }, { xAxis: string }]> {
  const areas: Array<[{ xAxis: string }, { xAxis: string }]> = [];
  let start: string | null = null;
  let last: string | null = null;
  for (const p of pts) {
    const label = fmtHour(p.timestamp);
    const breach = p.spread_bps != null && !p.up;
    if (breach) {
      if (start == null) start = label;
      last = label;
    } else if (start != null && last != null) {
      areas.push([{ xAxis: start }, { xAxis: last }]);
      start = null;
      last = null;
    }
  }
  if (start != null && last != null)
    areas.push([{ xAxis: start }, { xAxis: last }]);
  return areas;
}

// Per-side observation access for a band (actual sides, never derived).
function sideVal(
  p: SeriesPoint,
  band: "50bps" | "100bps" | "200bps",
  side: "bid" | "ask",
): number | null {
  const v = p[`depth_${band}_${side}_usd` as keyof SeriesPoint] as
    | number
    | null
    | undefined;
  return v ?? null;
}

function spreadOption(
  pts: SeriesPoint[],
  committed: number | null,
  label: string,
  ctx?: SeriesPoint[],
) {
  const labels = pts.map((p) => fmtHour(p.timestamp));
  const legend = [`${label} spread`, "effective cap (tick-quantized)"];
  const series: Record<string, unknown>[] = [
    {
      name: `${label} spread`,
      type: "line",
      data: pts.map((p) => p.spread_bps),
      showSymbol: false,
      lineStyle: { color: ACCENT, width: 1.5 },
      itemStyle: { color: ACCENT },
      markLine:
        committed != null
          ? {
              silent: true,
              symbol: "none",
              lineStyle: { color: MUTED, type: "dashed" },
              label: {
                color: MUTED,
                fontSize: 10,
                formatter: `raw cap ${committed} bps`,
              },
              data: [{ yAxis: committed }],
            }
          : undefined,
      markArea: {
        silent: true,
        itemStyle: { color: "rgba(224,112,112,0.14)" },
        data: breachAreas(pts),
      },
    },
    {
      name: "effective cap (tick-quantized)",
      type: "line",
      data: pts.map((p) => p.effective_cap_bps),
      showSymbol: false,
      step: "end",
      lineStyle: { color: AMBER, width: 1, type: "dashed" },
      itemStyle: { color: AMBER },
    },
  ];
  if (ctx) {
    legend.push("market spread");
    series.push({
      name: "market spread",
      type: "line",
      data: ctx.map((p) => p.spread_bps),
      showSymbol: false,
      lineStyle: { color: MUTED, width: 1, opacity: 0.6 },
      itemStyle: { color: MUTED },
    });
  }
  return {
    animation: false,
    grid: { left: 48, right: 16, top: 30, bottom: 24 },
    tooltip: { trigger: "axis" },
    legend: {
      data: legend,
      textStyle: { color: MUTED, fontSize: 10 },
      top: 0,
    },
    xAxis: { type: "category", data: labels, ...AXIS },
    yAxis: {
      type: "value",
      name: "bps",
      nameTextStyle: { color: MUTED },
      ...AXIS,
    },
    series,
  };
}

function depthOption(
  pts: SeriesPoint[],
  vc: VenueCompliance,
  label: string,
  ctx?: SeriesPoint[],
) {
  const labels = pts.map((p) => fmtHour(p.timestamp));
  const c = vc.commitments;
  // Per-side contracts render ACTUAL sides, order-book style: ask depth
  // above the zero axis, bid depth mirrored below, floors at ±floor.
  const perSide = c.depth_semantics === "per_side";
  const bands: Array<{
    name: string;
    color: string;
    key: "50bps" | "100bps" | "200bps";
    floor: number | null;
  }> = [
    {
      name: "50bps",
      color: GREEN,
      key: "50bps",
      floor: c.committed_depth_50bps_usd,
    },
    {
      name: "100bps",
      color: ACCENT,
      key: "100bps",
      floor: c.committed_depth_100bps_usd,
    },
    {
      name: "200bps",
      color: AMBER,
      key: "200bps",
      floor: c.committed_depth_200bps_usd,
    },
  ];
  const floorMark = (b: (typeof bands)[number]) =>
    b.floor != null
      ? {
          silent: true,
          symbol: "none",
          lineStyle: { color: b.color, type: "dashed", opacity: 0.7 },
          label: {
            color: b.color,
            fontSize: 10,
            formatter: `floor ${fmtUsd(b.floor)}`,
          },
          data: [{ yAxis: b.floor }],
        }
      : undefined;
  const series: Record<string, unknown>[] = [];
  const legend: string[] = [];
  if (!perSide) {
    for (const b of bands) {
      const name = `${b.name} depth`;
      legend.push(name);
      series.push({
        name,
        type: "line",
        data: pts.map(
          (p) =>
            p[`depth_${b.key}_usd` as keyof SeriesPoint] as number | null,
        ),
        showSymbol: false,
        lineStyle: { color: b.color, width: 1.5 },
        itemStyle: { color: b.color },
        markLine: floorMark(b),
      });
    }
  } else {
    // Mirrored order book: ask positive, bid negated; floors at ±floor.
    const mirrorFloor = (b: (typeof bands)[number], sign: 1 | -1) =>
      b.floor != null
        ? {
            silent: true,
            symbol: "none",
            lineStyle: { color: b.color, type: "dashed", opacity: 0.7 },
            label: {
              color: b.color,
              fontSize: 10,
              formatter: sign > 0 ? `ask floor ${fmtUsd(b.floor)}` : `bid floor ${fmtUsd(b.floor)}`,
            },
            data: [{ yAxis: sign * b.floor }],
          }
        : undefined;
    for (const b of bands) {
      const askName = `${label} ${b.name} ask`;
      const bidName = `${label} ${b.name} bid`;
      legend.push(askName, bidName);
      series.push({
        name: askName,
        type: "line",
        data: pts.map((p) => sideVal(p, b.key, "ask")),
        showSymbol: false,
        lineStyle: { color: b.color, width: 1.5 },
        itemStyle: { color: b.color },
        markLine: mirrorFloor(b, 1),
      });
      series.push({
        name: bidName,
        type: "line",
        data: pts.map((p) => {
          const v = sideVal(p, b.key, "bid");
          return v == null ? null : -v;
        }),
        showSymbol: false,
        lineStyle: { color: b.color, width: 1.5, opacity: 0.8 },
        itemStyle: { color: b.color },
        markLine: mirrorFloor(b, -1),
      });
    }
    if (ctx) {
      for (const b of bands) {
        const askName = `market ${b.name} ask`;
        const bidName = `market ${b.name} bid`;
        legend.push(askName, bidName);
        series.push({
          name: askName,
          type: "line",
          data: ctx.map((p) => sideVal(p, b.key, "ask")),
          showSymbol: false,
          lineStyle: { color: b.color, width: 1, type: "dotted", opacity: 0.45 },
          itemStyle: { color: b.color },
        });
        series.push({
          name: bidName,
          type: "line",
          data: ctx.map((p) => {
            const v = sideVal(p, b.key, "bid");
            return v == null ? null : -v;
          }),
          showSymbol: false,
          lineStyle: { color: b.color, width: 1, type: "dotted", opacity: 0.45 },
          itemStyle: { color: b.color },
        });
      }
    }
  }
  // Axis bounds must cover the FLOORS, not just the data — otherwise a
  // floor above observed depth (the breach case) renders off-chart.
  const extent: number[] = [];
  for (const s of series) {
    for (const v of s.data as Array<number | null>) {
      if (v != null) extent.push(Math.abs(v));
    }
  }
  for (const b of bands) {
    if (b.floor != null) extent.push(b.floor);
  }
  const rawMax = extent.length > 0 ? Math.max(...extent) : 1;
  const yMax = Math.ceil((rawMax * 1.15) / 1000) * 1000 || 1000;
  return {
    animation: false,
    grid: { left: 62, right: 16, top: 30, bottom: 24 },
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: unknown) =>
        typeof v === "number" ? fmtUsd(Math.abs(v)) : "—",
    },
    legend: {
      data: legend,
      textStyle: { color: MUTED, fontSize: 10 },
      top: 0,
    },
    xAxis: { type: "category", data: labels, ...AXIS },
    yAxis: {
      type: "value",
      min: perSide ? -yMax : 0,
      max: yMax,
      name: perSide ? "ask ↑ / bid ↓ USD" : "USD",
      nameTextStyle: { color: MUTED },
      axisLabel: {
        color: MUTED,
        fontSize: 10,
        formatter: (v: number) => {
          const a = Math.abs(v);
          return a >= 1000 ? `${a / 1000}k` : `${a}`;
        },
      },
      axisLine: AXIS.axisLine,
      splitLine: AXIS.splitLine,
    },
    series,
  };
}

function rollingUptime(pts: SeriesPoint[]): number[] {
  const out: number[] = [];
  let upCount = 0;
  for (let i = 0; i < pts.length; i++) {
    if (pts[i].up) upCount++;
    if (i >= 24 && pts[i - 24].up) upCount--;
    const span = Math.min(i + 1, 24);
    out.push((100 * upCount) / span);
  }
  return out;
}

// Display bucketing: average k consecutive hourly points so charts read
// as trends (7d -> 6h buckets, 14d -> 12h, 30d -> daily). Evaluation is
// untouched — this is presentation only.
function bucketK(windowDays: number): number {
  return windowDays >= 30 ? 24 : windowDays >= 14 ? 12 : 6;
}
function bucketize(pts: SeriesPoint[], k: number): SeriesPoint[] {
  if (k <= 1) return pts;
  const out: SeriesPoint[] = [];
  const numKeys: Array<keyof SeriesPoint> = [
    "spread_bps",
    "floor_bps",
    "effective_cap_bps",
    "mid_price",
    "volume_usd",
    "depth_50bps_usd",
    "depth_100bps_usd",
    "depth_200bps_usd",
    "attributed_two_sided_pct",
    "depth_50bps_bid_usd",
    "depth_50bps_ask_usd",
    "depth_100bps_bid_usd",
    "depth_100bps_ask_usd",
    "depth_200bps_bid_usd",
    "depth_200bps_ask_usd",
  ];
  for (let i = 0; i < pts.length; i += k) {
    const chunk = pts.slice(i, i + k);
    const agg: Record<string, unknown> = {
      timestamp: chunk[0].timestamp,
      up: chunk.filter((p) => p.up).length >= chunk.length / 2,
    };
    for (const key of numKeys) {
      let sum = 0;
      let n = 0;
      for (const p of chunk) {
        const v = p[key] as number | null | undefined;
        if (v != null) {
          sum += v;
          n++;
        }
      }
      agg[key as string] = n > 0 ? sum / n : null;
    }
    out.push(agg as unknown as SeriesPoint);
  }
  return out;
}

// Rolling 24h attributed presence — fractional per-hour two-sided credit
// (an hour with no attributed evidence earns 0, honestly).
function rollingPresence(apts: SeriesPoint[]): number[] {
  const credit = apts.map((p) => (p.attributed_two_sided_pct ?? 0) / 100);
  const out: number[] = [];
  let sum = 0;
  for (let i = 0; i < credit.length; i++) {
    sum += credit[i];
    if (i >= 24) sum -= credit[i - 24];
    const span = Math.min(i + 1, 24);
    out.push((100 * sum) / span);
  }
  return out;
}

function uptimeOption(
  rawPts: SeriesPoint[],
  committed: number | null,
  label: string,
  presenceMode: boolean,
  k: number,
) {
  // Rolling computed on RAW hourly points, then sampled at bucket
  // boundaries so the line matches the other charts' time base.
  const rolling = presenceMode ? rollingPresence(rawPts) : rollingUptime(rawPts);
  const idxs: number[] = [];
  for (let i = 0; i < rawPts.length; i += k) {
    idxs.push(Math.min(i + k - 1, rawPts.length - 1));
  }
  const labels = idxs.map((i) => fmtHour(rawPts[Math.max(i - k + 1, 0)].timestamp));
  const data = idxs.map((i) => rolling[i]);
  const name = presenceMode
    ? `${label} presence 24h`
    : "rolling 24h uptime";
  const legend = [name];
  const series: Record<string, unknown>[] = [
    {
      name,
      type: "line",
      data,
      showSymbol: false,
      lineStyle: { color: GREEN, width: 1.5 },
      itemStyle: { color: GREEN },
      areaStyle: { color: "rgba(111,207,151,0.08)" },
      markLine:
        committed != null
          ? {
              silent: true,
              symbol: "none",
              lineStyle: { color: RED, type: "dashed" },
              label: {
                color: RED,
                fontSize: 10,
                formatter: `committed ${committed}%`,
              },
              data: [{ yAxis: committed }],
            }
          : undefined,
    },
  ];
  return {
    animation: false,
    grid: { left: 48, right: 16, top: 26, bottom: 24 },
    tooltip: { trigger: "axis" },
    legend: {
      data: legend,
      textStyle: { color: MUTED, fontSize: 10 },
      top: 0,
    },
    xAxis: { type: "category", data: labels, ...AXIS },
    yAxis: {
      type: "value",
      min: 0,
      max: 100,
      name: "%",
      nameTextStyle: { color: MUTED },
      ...AXIS,
    },
    series,
  };
}

function CoverageChip({ cov }: { cov: ComplianceCoverage | undefined }) {
  if (!cov) return null;
  const ok = cov.status === "resolved";
  return (
    <span style={chip(ok ? GREEN : AMBER)}>
      {ok ? `${cov.obs_count} obs` : cov.status}
    </span>
  );
}

function VenueCard({
  venueKey,
  data,
  cov,
  pts,
  windowDays,
}: {
  venueKey: string;
  data: VenueCompliance;
  cov: ComplianceCoverage | undefined;
  pts?: SeriesPoint[];
  windowDays: number;
}) {
  const resolved = cov?.status === "resolved";
  const bands = data.depth.per_band;
  const bandOrder = Object.keys(bands).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10),
  );
  // committed · current · window-average per KPI. Per-side contracts use
  // ACTUAL side observations (bid/ask), never a symmetric approximation.
  const perSide = data.commitments.depth_semantics === "per_side";
  const spreadNow = lastNonNull(pts, (p) => p.spread_bps);
  const spreadAvg = avgNonNull(pts, (p) => p.spread_bps);
  const totalGet: Record<string, (p: SeriesPoint) => number | null> = {
    "50bps": (p) => p.depth_50bps_usd,
    "100bps": (p) => p.depth_100bps_usd,
    "200bps": (p) => p.depth_200bps_usd,
  };
  const bidGet: Record<string, (p: SeriesPoint) => number | null> = {
    "50bps": (p) => p.depth_50bps_bid_usd ?? null,
    "100bps": (p) => p.depth_100bps_bid_usd ?? null,
    "200bps": (p) => p.depth_200bps_bid_usd ?? null,
  };
  const askGet: Record<string, (p: SeriesPoint) => number | null> = {
    "50bps": (p) => p.depth_50bps_ask_usd ?? null,
    "100bps": (p) => p.depth_100bps_ask_usd ?? null,
    "200bps": (p) => p.depth_200bps_ask_usd ?? null,
  };
  const uptimeMode = data.uptime.definition_mode;
  const uptimeLabel =
    uptimeMode === "presence"
      ? "MM two-sided presence (read-only keys)"
      : uptimeMode === "presence_market"
        ? "market two-sided quote presence — MM view via attributed selector"
        : `${data.uptime.up_hours} / ${data.uptime.expected_hours} hrs up`;
  return (
    <div style={card}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 14, color: TEXT, fontWeight: 600 }}>
          {venueLabel(venueKey)}
        </span>
        <CoverageChip cov={cov} />
      </div>
      {!resolved ? (
        <div style={{ marginTop: 8, fontSize: 12, color: MUTED }}>
          {cov?.detail ?? "no coverage detail"}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 28,
              marginTop: 10,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: MUTED }}>
                spread vs {data.spread.committed_spread_bps ?? "—"} bps
                committed
              </div>
              <div
                style={{
                  fontSize: 22,
                  color: scoreColor(data.spread.compliance_score),
                }}
              >
                {fmtScore(data.spread.compliance_score)}
              </div>
              <div style={{ fontSize: 11, color: MUTED }}>
                {data.spread.compliant_observations ?? "—"} /{" "}
                {data.spread.total_observations ?? "—"} hrs compliant
              </div>
              <div style={{ fontSize: 11, color: MUTED }}>
                now {spreadNow == null ? "—" : `${spreadNow.toFixed(1)} bps`}{" "}
                · avg {spreadAvg == null ? "—" : `${spreadAvg.toFixed(1)} bps`}
              </div>
              <div style={{ fontSize: 11, color: MUTED, marginTop: 10 }}>
                uptime vs {data.uptime.committed_uptime_pct ?? "—"}% committed
              </div>
              <div
                style={{
                  fontSize: 22,
                  color: uptimeColor(
                    data.uptime.overall_uptime_pct,
                    data.uptime.committed_uptime_pct,
                  ),
                }}
              >
                {data.uptime.overall_uptime_pct == null
                  ? "—"
                  : `${data.uptime.overall_uptime_pct.toFixed(1)}%`}
              </div>
              <div style={{ fontSize: 11, color: MUTED }}>
                {uptimeLabel}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: MUTED }}>depth aggregate</div>
              <div
                style={{
                  fontSize: 22,
                  color: scoreColor(data.depth.aggregate_compliance),
                }}
              >
                {fmtScore(data.depth.aggregate_compliance)}
              </div>
              <table
                style={{
                  borderCollapse: "collapse",
                  marginTop: 6,
                  width: "100%",
                }}
              >
                <thead>
                  <tr>
                    <th style={th}>band</th>
                    <th style={th}>{perSide ? "floor /side" : "committed"}</th>
                    {perSide ? (
                      <>
                        <th style={th}>ask avg {windowDays}d</th>
                        <th style={th}>bid avg {windowDays}d</th>
                      </>
                    ) : (
                      <>
                        <th style={th}>now</th>
                        <th style={th}>avg {windowDays}d</th>
                      </>
                    )}
                    <th style={th}>score</th>
                    <th style={th}>hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {bandOrder.map((b) => {
                    const floor = bands[b].committed_usd;
                    const cc = (v: number | null) =>
                      v == null ? MUTED : v >= floor ? GREEN : RED;
                    const askAvg = avgNonNull(pts, askGet[b] ?? (() => null));
                    const bidAvg = avgNonNull(pts, bidGet[b] ?? (() => null));
                    const totNow = lastNonNull(pts, totalGet[b] ?? (() => null));
                    const totAvg = avgNonNull(pts, totalGet[b] ?? (() => null));
                    return (
                      <tr key={b}>
                        <td
                          style={{
                            ...td,
                            color: MUTED,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {b}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {fmtUsd(floor)}
                        </td>
                        {perSide ? (
                          <>
                            <td
                              style={{
                                ...td,
                                color: cc(askAvg),
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtUsd(askAvg)}
                            </td>
                            <td
                              style={{
                                ...td,
                                color: cc(bidAvg),
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fmtUsd(bidAvg)}
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ ...td, color: cc(totNow) }}>
                              {fmtUsd(totNow)}
                            </td>
                            <td style={{ ...td, color: cc(totAvg) }}>
                              {fmtUsd(totAvg)}
                            </td>
                          </>
                        )}
                        <td
                          style={{
                            ...td,
                            color: scoreColor(bands[b].compliance_score),
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmtScore(bands[b].compliance_score)}
                        </td>
                        <td
                          style={{
                            ...td,
                            color: MUTED,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {bands[b].compliant_observations ?? "—"}/
                          {bands[b].valid_observations ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function VenueCharts({
  venueKey,
  pts,
  ctx,
  vc,
  anchoring,
  label,
  presenceMode,
  windowDays,
}: {
  venueKey: string;
  pts: SeriesPoint[];
  ctx?: SeriesPoint[];
  vc: VenueCompliance;
  anchoring: string;
  label: string;
  presenceMode: boolean;
  windowDays: number;
}) {
  const committedSpread = vc.commitments.committed_spread_bps;
  const committedUptime = vc.commitments.committed_uptime_pct;
  const perSide = vc.commitments.depth_semantics === "per_side";
  const k = bucketK(windowDays);
  const bpts = bucketize(pts, k);
  const bctx = ctx ? bucketize(ctx, k) : undefined;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={card}>
        <div style={{ fontSize: 13, color: TEXT, fontWeight: 600 }}>
          {venueLabel(venueKey)} — spread vs committed cap
        </div>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>
          raw cap dashed grey · tick-quantized effective cap dashed amber ·
          breach intervals shaded red · {k}h display buckets
          {bctx ? " · market context thin grey" : ""}
        </div>
        <ReactECharts
          option={spreadOption(bpts, committedSpread, label, bctx)}
          style={{ height: 240 }}
          notMerge
        />
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, color: TEXT, fontWeight: 600 }}>
          {venueLabel(venueKey)} — depth vs committed floors
        </div>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>
          {anchoring} · semantics:{" "}
          {vc.commitments.depth_semantics ?? "total"}
          {perSide
            ? " · actual sides, ask above / bid below, floors at ±floor; each side must independently meet its floor"
            : " (bid+ask summed)"}
        </div>
        <ReactECharts
          option={depthOption(bpts, vc, label)}
          style={{ height: perSide ? 300 : 240 }}
          notMerge
        />
      </div>
      <div style={card}>
        <div style={{ fontSize: 13, color: TEXT, fontWeight: 600 }}>
          {venueLabel(venueKey)} — rolling 24h uptime
        </div>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>
          {presenceMode
            ? `${label} two-sided quoting presence (read-only keys), rolling 24h`
            : vc.uptime.definition}
        </div>
        <ReactECharts
          option={uptimeOption(pts, committedUptime, label, presenceMode, k)}
          style={{ height: 180 }}
          notMerge
        />
      </div>
    </div>
  );
}

export function MonitorTab({ identity }: { identity: TabIdentity }) {
  const isOperator = identity.client_id == null;
  const [pickedClient, setPickedClient] = useState<string | null>(null);
  const [pickedRfp, setPickedRfp] = useState<string | null>(null);
  const [pickedChartVenue, setPickedChartVenue] = useState<string | null>(null);
  const [pickedMm, setPickedMm] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(7);

  const clientsQ = useQuery({
    queryKey: ["lge-clients"],
    queryFn: fetchClients,
    enabled: isOperator,
  });
  const clientId =
    identity.client_id ??
    pickedClient ??
    clientsQ.data?.clients.find((c) => c.engagement_count > 0)?.client_id ??
    null;

  const engagementsQ = useQuery({
    queryKey: ["lge-engagements", clientId],
    queryFn: () => fetchEngagements(clientId!),
    enabled: clientId != null,
  });
  const engagements = engagementsQ.data?.engagements ?? [];
  const rfpId = pickedRfp ?? engagements[0]?.rfp_id ?? null;

  const complianceQ = useQuery({
    queryKey: ["lge-compliance", clientId, rfpId, windowDays],
    queryFn: () =>
      fetchEngagementCompliance(clientId!, rfpId!, windowDays, true),
    enabled: clientId != null && rfpId != null,
    retry: false,
  });
  const d = complianceQ.data;

  // LIQ-80 inc 3: attributed MM discovery + overlay query.
  const mmsQ = useQuery({
    queryKey: ["lge-attributed-mms", clientId],
    queryFn: () => fetchAttributedMms(clientId!),
    enabled: clientId != null,
  });
  const mms = mmsQ.data?.mms ?? [];
  // MM-primary lens: when attributed MMs exist, the tab shows the MM's own
  // read-only-key data by default; market series become chart context.
  const mm =
    mms.length > 0
      ? pickedMm != null && mms.includes(pickedMm)
        ? pickedMm
        : mms[0]
      : null;

  const attributedQ = useQuery({
    queryKey: ["lge-compliance-attr", clientId, rfpId, mm, windowDays],
    queryFn: () =>
      fetchEngagementComplianceAttributed(
        clientId!,
        rfpId!,
        mm!,
        windowDays,
        true,
      ),
    enabled: clientId != null && rfpId != null && mm != null,
    retry: false,
  });
  const ad = mm != null ? attributedQ.data : undefined;
  // Primary response drives cards, scores, and chart emphasis; market data
  // remains available as chart context when an MM is primary.
  const primary = mm != null && ad ? ad : d;
  const presenceMode = mm != null && ad != null;

  const resolvedKeys = primary
    ? Object.keys(primary.venues).filter(
        (vk) =>
          primary.coverage[vk]?.status === "resolved" &&
          (primary.series?.[vk]?.length ?? 0) > 0,
      )
    : [];
  const chartVenue =
    pickedChartVenue && resolvedKeys.includes(pickedChartVenue)
      ? pickedChartVenue
      : (resolvedKeys[0] ?? null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {isOperator && (
          <select
            style={selectStyle}
            value={clientId ?? ""}
            onChange={(e) => {
              setPickedClient(e.target.value || null);
              setPickedRfp(null);
              setPickedChartVenue(null);
              setPickedMm(null);
            }}
          >
            <option value="">select client…</option>
            {(clientsQ.data?.clients ?? []).map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.display_name} ({c.engagement_count})
              </option>
            ))}
          </select>
        )}
        {engagements.length > 1 && (
          <select
            style={selectStyle}
            value={rfpId ?? ""}
            onChange={(e) => {
              setPickedRfp(e.target.value || null);
              setPickedChartVenue(null);
            }}
          >
            {engagements.map((e) => (
              <option key={e.rfp_id} value={e.rfp_id}>
                {e.winner || e.rfp_id}
              </option>
            ))}
          </select>
        )}
        {mms.length > 0 && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: MUTED }}>Market Maker</span>
            <select
              style={selectStyle}
              value={mm ?? ""}
              onChange={(e) => setPickedMm(e.target.value || null)}
              title="Market maker (read-only-key attributed data)"
            >
              {mms.map((m) => (
                <option key={m} value={m}>
                  {m.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {(complianceQ.isLoading || (mm != null && attributedQ.isLoading)) && (
        <div style={{ color: MUTED, fontSize: 13 }}>
          evaluating compliance window…
        </div>
      )}
      {complianceQ.error != null && (
        <div style={{ ...card, borderColor: `${AMBER}55` }}>
          <span style={{ color: AMBER, fontSize: 13 }}>
            {complianceQ.error instanceof ApiError &&
            complianceQ.error.status === 404
              ? "No frozen monitoring contract for this engagement — the contract is generated at award."
              : complianceQ.error instanceof ApiError &&
                  complianceQ.error.status === 409
                ? "Contract integrity check FAILED — the frozen contract file does not match its stored hash. Do not act on any numbers until this is investigated."
                : `Compliance evaluation failed: ${String(complianceQ.error)}`}
          </span>
        </div>
      )}
      {mm != null && attributedQ.error != null && (
        <div style={{ ...card, borderColor: `${AMBER}55` }}>
          <span style={{ color: AMBER, fontSize: 13 }}>
            Attributed data unavailable for {mm} — showing market data.{" "}
            {attributedQ.error instanceof ApiError
              ? attributedQ.error.message
              : String(attributedQ.error)}
          </span>
        </div>
      )}

      {primary && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(340px, 100%), 1fr))",
              gap: 14,
            }}
          >
            {Object.keys(primary.venues).map((vk) => (
              <VenueCard
                key={vk}
                venueKey={vk}
                data={primary.venues[vk]}
                cov={primary.coverage[vk]}
                pts={primary.series?.[vk]}
                windowDays={windowDays}
              />
            ))}
          </div>
          {resolvedKeys.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {resolvedKeys.map((vk) => (
                  <button
                    key={vk}
                    onClick={() => setPickedChartVenue(vk)}
                    style={{
                      ...chip(chartVenue === vk ? ACCENT : MUTED),
                      background: "transparent",
                      cursor: "pointer",
                    }}
                  >
                    {venueLabel(vk)}
                  </button>
                ))}
                <span
                  style={{
                    fontSize: 12,
                    color: MUTED,
                    marginLeft: 14,
                  }}
                >
                  window
                </span>
                {WINDOW_CHOICES.map((w) => (
                  <button
                    key={w}
                    onClick={() => setWindowDays(w)}
                    style={{
                      ...chip(windowDays === w ? ACCENT : MUTED),
                      background: "transparent",
                      cursor: "pointer",
                    }}
                  >
                    {w}d
                  </button>
                ))}
              </div>
              {chartVenue && primary.series?.[chartVenue] && (
                <VenueCharts
                  venueKey={chartVenue}
                  pts={primary.series[chartVenue]}
                  ctx={
                    presenceMode ? d?.series?.[chartVenue] : undefined
                  }
                  vc={primary.venues[chartVenue]}
                  anchoring={primary.depth_band_anchoring}
                  label={mm != null ? mm.toUpperCase() : "measured"}
                  presenceMode={presenceMode}
                  windowDays={windowDays}
                />
              )}
            </>
          )}
          <div style={{ fontSize: 11, color: MUTED }}>
            {primary.contract.contract_id} · hash{" "}
            {primary.contract.contract_hash.slice(0, 16)}… · integrity
            verified · window {fmtTs(primary.window.start)} →{" "}
            {fmtTs(primary.window.end)} · evaluated{" "}
            {fmtTs(primary.evaluated_at)}
          </div>
        </>
      )}
    </div>
  );
}
